import { useState, useEffect } from 'react';
import { Calendar, Clock, Globe, Award, Users, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { contentApi } from '../lib/api';
import SiteNav from '../components/SiteNav';

export default function LandingPage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await contentApi.getLandingContent();
        setContent(response.data);
        
        // Setup countdown
        if (response.data.settings?.start_date) {
          const startDate = new Date(response.data.settings.start_date);
          updateCountdown(startDate);
          const interval = setInterval(() => updateCountdown(startDate), 1000);
          return () => clearInterval(interval);
        }
      } catch (error) {
        console.error('Failed to load content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const updateCountdown = (targetDate) => {
    const now = new Date();
    const diff = targetDate - now;
    
    if (diff > 0) {
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        mins: Math.floor((diff / (1000 * 60)) % 60),
        secs: Math.floor((diff / 1000) % 60)
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#9A3B26] border-t-transparent"></div>
      </div>
    );
  }

  const settings = content?.settings || {};
  const sections = content?.sections || {};
  const curriculum = content?.curriculum || [];
  const testimonials = content?.testimonials || [];
  const faqs = content?.faqs || [];
  const instructors = content?.instructors || [];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <SiteNav />
      <div className="pt-16">
      {/* Hero Section */}
      <HeroSection countdown={countdown} settings={settings} heroSection={sections.hero} />
      
      {/* Programme Details */}
      <ProgrammeDetails settings={settings} />
      
      {/* Benefits */}
      <Benefits />
      
      {/* Curriculum */}
      <Curriculum curriculum={curriculum} />
      
      {/* Testimonials */}
      <Testimonials testimonials={testimonials} />
      
      {/* Who Is This For */}
      <WhoIsThisFor />
      
      {/* Instructors */}
      <Instructors instructors={instructors} />
      
      {/* FAQs */}
      <FAQs faqs={faqs} />
      
      {/* Registration CTA */}
      <RegistrationCTA settings={settings} />
      
      {/* Footer */}
      <Footer />
      </div>
    </div>
  );
}

// Hero Section Component
function HeroSection({ countdown, settings, heroSection }) {
  const scrollToLogin = () => {
    document.getElementById('register-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const heroContent = heroSection?.content || {};
  const badgeText = heroContent.badge_text || '21-Day Transformation Programme';
  const heading = heroContent.heading || 'Awaken the Warrior Within';
  const highlightWord = heroContent.highlight_word || 'Warrior';
  const subheading = heroContent.subheading || 'Discover the ancient martial art of Kalaripayattu. Transform your body, sharpen your mind, and unlock your inner strength through 21 days of guided practice.';
  const heroImageUrl = heroContent.hero_image_url || 'https://images.unsplash.com/photo-1610308235436-3e1cde290a87?w=800&q=80';
  const joinedText = heroContent.joined_text || '2500+ people already joined';

  const headingParts = heading.split(highlightWord);

  return (
    <section className="hero-section relative px-6 md:px-12 lg:px-24 py-12 md:py-20">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8 animate-fade-in-up">
            <div className="inline-block bg-[#F4EFE6] text-[#9A3B26] rounded-full px-4 py-2 text-sm font-medium tracking-wide">
              {badgeText}
            </div>
            
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-[#292524] leading-tight">
              {headingParts.length > 1 ? (
                <>
                  {headingParts[0]}
                  <span className="text-[#9A3B26]">{highlightWord}</span>
                  {headingParts.slice(1).join(highlightWord)}
                </>
              ) : (
                heading
              )}
            </h1>
            
            <p className="text-lg md:text-xl text-[#78716C] max-w-lg font-body">
              {subheading}
            </p>
            
            <div className="flex flex-wrap items-center gap-4">
              <Button
                data-testid="hero-register-btn"
                onClick={scrollToLogin}
                className="bg-[#9A3B26] hover:bg-[#7A2E1E] text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Login to Programme
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
              <a href="/kalaripayattu" className="inline-flex items-center gap-2 border border-[#9A3B26]/40 text-[#9A3B26] rounded-full px-8 py-6 text-lg font-medium hover:bg-[#9A3B26]/5 transition-colors">
                What is Kalaripayattu?
              </a>
            </div>
            
            <div className="flex items-center gap-3 text-[#78716C]">
              <Users className="h-5 w-5" />
              <span className="font-medium">{joinedText}</span>
            </div>
          </div>
          
          {/* Right Image */}
          <div className="relative animate-fade-in-up stagger-2">
            <div className="hero-image-frame relative z-10">
              <img 
                src={heroImageUrl}
                alt="Kalaripayattu martial arts"
                className="w-full h-[500px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#DDA15E] organic-blob opacity-60 z-0"></div>
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#2C5E4B] organic-blob opacity-40 z-0"></div>
          </div>
        </div>
        
        {/* Countdown Timer */}
        <div className="mt-16 flex justify-center">
          <div className="countdown-box rounded-2xl p-6 md:p-8 flex flex-wrap justify-center gap-6 md:gap-10">
            <div className="text-center">
              <p className="text-xs uppercase tracking-widest text-[#78716C] mb-2">Workshop Starting In</p>
            </div>
            <div className="w-full flex justify-center gap-6 md:gap-10">
              {[
                { value: countdown.days, label: 'Days' },
                { value: countdown.hours, label: 'Hours' },
                { value: countdown.mins, label: 'Mins' },
                { value: countdown.secs, label: 'Secs' }
              ].map((item, idx) => (
                <div key={idx} className="text-center">
                  <div className="countdown-number text-4xl md:text-5xl font-bold text-[#9A3B26]">
                    {String(item.value).padStart(2, '0')}
                  </div>
                  <div className="text-sm text-[#78716C] mt-1">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Programme Details Component
function ProgrammeDetails({ settings }) {
  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    const date = new Date(dateStr);
    const datePart = date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
    const timePart = date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
    return `${datePart}, ${timePart} IST`;
  };

  const details = [
    { 
      icon: Calendar, 
      title: 'Start Date', 
      value: formatDate(settings?.start_date),
      subtitle: 'Mark your calendar!'
    },
    { 
      icon: Award, 
      title: 'Level', 
      value: settings?.level || 'Beginner',
      subtitle: 'Perfect for first-timers'
    },
    { 
      icon: Clock, 
      title: 'Time Slots', 
      value: settings?.time_slots?.[0]?.time || '6:00-7:30 AM',
      subtitle: `Recording available for ${settings?.recording_access_hours || 24}hrs`
    },
    { 
      icon: Globe, 
      title: 'Languages', 
      value: settings?.languages?.join(' | ') || 'English | Malayalam',
      subtitle: 'Simple & easy to understand'
    }
  ];

  return (
    <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#F4EFE6]">
      <div className="max-w-7xl mx-auto">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {details.map((detail, idx) => (
            <div 
              key={idx}
              className="detail-card animate-fade-in-up"
              style={{ animationDelay: `${idx * 0.1}s` }}
            >
              <div className="detail-card-icon mb-4">
                <detail.icon className="h-6 w-6" />
              </div>
              <h3 className="text-sm uppercase tracking-widest text-[#78716C] mb-2">{detail.title}</h3>
              <p className="font-heading text-xl font-bold text-[#292524]">{detail.value}</p>
              <p className="text-sm text-[#78716C] mt-1">{detail.subtitle}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// What's Included Component
function WhatsIncluded() {
  const items = [
    {
      title: '90-Minute Session Daily',
      description: 'Asanas, meditation, pranayama, and martial arts techniques — all in one powerful session.',
      image: 'https://images.unsplash.com/photo-1504686740608-f31434d038e6?w=400&q=80'
    },
    {
      title: 'Ancient Wisdom Made Simple',
      description: 'Learn the philosophy and history of Kalaripayattu through engaging stories and practices.',
      image: 'https://images.unsplash.com/photo-1503192259632-12154871ab1f?w=400&q=80'
    },
    {
      title: 'Healing Lifestyle Guidance',
      description: 'Discover traditional diet plans and lifestyle practices that complement your martial arts journey.',
      image: 'https://images.unsplash.com/photo-1565419672978-6ba05e8859d5?w=400&q=80'
    },
    {
      title: 'Session Recordings',
      description: 'Missed a session? Access recordings for 24 hours so you never fall behind.',
      image: 'https://images.unsplash.com/photo-1703245588712-825b1cc9d6dd?w=400&q=80'
    }
  ];

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-center text-[#292524] mb-4">
          What's Included?
        </h2>
        <p className="text-center text-[#78716C] mb-12 max-w-2xl mx-auto">
          Everything you need for a complete transformation
        </p>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map((item, idx) => (
            <div 
              key={idx}
              className="bg-white border border-[#E7E5E4] rounded-xl overflow-hidden card-hover animate-fade-in-up"
              style={{ animationDelay: `${idx * 0.1}s` }}
            >
              {item.image ? (
                <img src={item.image} alt={item.title} className="w-full h-48 object-cover" />
              ) : (
                <div className="w-full h-48 bg-[#2C5E4B] flex items-center justify-center">
                  <Award className="h-16 w-16 text-white opacity-80" />
                </div>
              )}
              <div className="p-6">
                <h3 className="font-heading text-xl font-bold text-[#292524] mb-2">{item.title}</h3>
                <p className="text-[#78716C]">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Benefits Component
function Benefits() {
  const benefits = [
    { highlight: 'higher energy', text: 'Experience', suffix: 'levels' },
    { highlight: 'body mechanics', text: 'Understand your', suffix: '' },
    { highlight: 'mental clarity', text: 'Achieve', suffix: '& focus' },
    { highlight: 'self-defense', text: 'Learn practical', suffix: 'skills' },
    { highlight: 'flexibility', text: 'Improve your', suffix: '& strength' },
    { highlight: 'inner peace', text: 'Cultivate', suffix: '& discipline' }
  ];

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F4EFE6]">
      <div className="max-w-7xl mx-auto">
        <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-center text-[#292524] mb-4">
          Benefits You Will Gain
        </h2>
        <p className="text-center text-[#78716C] mb-12">After following the teachings</p>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, idx) => (
            <div 
              key={idx}
              className="bg-white p-6 rounded-xl border border-[#E7E5E4] card-hover animate-fade-in-up"
              style={{ animationDelay: `${idx * 0.1}s` }}
            >
              <div className="text-5xl font-heading font-bold text-[#DDA15E] mb-4">
                {String(idx + 1).padStart(2, '0')}
              </div>
              <p className="text-lg text-[#292524]">
                {benefit.text} <span className="font-bold text-[#9A3B26]">{benefit.highlight}</span> {benefit.suffix}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Curriculum Component (imported separately)
import CurriculumSection from './landing/CurriculumSection';
function Curriculum({ curriculum }) {
  return <CurriculumSection curriculum={curriculum} />;
}

// Testimonials Component (imported separately)
import TestimonialsSection from './landing/TestimonialsSection';
function Testimonials({ testimonials }) {
  return <TestimonialsSection testimonials={testimonials} />;
}

// Who Is This For Component
function WhoIsThisFor() {
  const audiences = [
    { title: 'Working Professionals', description: 'Too busy for health? Just 90 minutes each morning can transform your life!' },
    { title: 'Fitness Enthusiasts', description: 'Looking to add a traditional martial art to your fitness routine? This is perfect!' },
    { title: 'Students', description: 'Need energy and focus to achieve your goals? Build discipline and strength!' },
    { title: 'Senior Citizens', description: 'Want gentle yet effective exercises? Learn at your own pace with modifications.' },
    { title: 'Stress Relief Seekers', description: "Feel overwhelmed? Find balance through ancient breathing and movement practices." },
    { title: 'Spiritual Seekers', description: 'Looking to deepen your practice? Connect body, mind, and spirit through Kalaripayattu.' }
  ];

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-center text-[#292524] mb-12">
          Who Is This For?
        </h2>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {audiences.map((item, idx) => (
            <div 
              key={idx}
              className="bg-white p-6 rounded-xl border border-[#E7E5E4] card-hover"
            >
              <div className="w-12 h-12 bg-[#2C5E4B] rounded-xl flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold text-[#292524] mb-2">{item.title}</h3>
              <p className="text-[#78716C]">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Instructors Component
function Instructors({ instructors }) {
  if (!instructors || instructors.length === 0) return null;

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-[#1C1917]">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-xs uppercase tracking-[0.25em] text-[#DDA15E]/80 mb-3">Your Teachers</p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white">
            Meet Your Guide
          </h2>
        </div>

        <div className={`grid gap-8 ${instructors.length === 1 ? 'max-w-md mx-auto' : instructors.length === 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
          {instructors.map((instructor, idx) => (
            <div key={idx} className="group relative">
              {/* Photo card */}
              <div className="relative rounded-2xl overflow-hidden aspect-[3/4] bg-[#2A1F1A]">
                {instructor.image_url ? (
                  <img
                    src={instructor.image_url}
                    alt={instructor.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#9A3B26]/40 to-[#2A1F1A]">
                    <div className="w-24 h-24 rounded-full bg-[#9A3B26]/30 border border-[#9A3B26]/40 flex items-center justify-center mb-3">
                      <span className="text-5xl font-heading font-bold text-white/60">
                        {instructor.name?.charAt(0) || 'G'}
                      </span>
                    </div>
                    <p className="text-white/30 text-sm">Photo coming soon</p>
                  </div>
                )}
                {/* Gradient overlay at bottom */}
                <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#1C1917] via-[#1C1917]/60 to-transparent" />
                {/* Name + title over image */}
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <h3 className="font-heading text-xl font-bold text-white leading-tight">{instructor.name}</h3>
                  {instructor.title && (
                    <p className="text-[#DDA15E] text-sm font-medium mt-0.5">{instructor.title}</p>
                  )}
                </div>
              </div>

              {/* Bio below card */}
              <div className="mt-4 px-1">
                <p className="text-[#E8D8BE]/70 text-sm leading-relaxed">{instructor.bio}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// FAQs Component (imported separately)
import FAQSection from './landing/FAQSection';
function FAQs({ faqs }) {
  return <FAQSection faqs={faqs} />;
}

// Registration CTA Component (imported separately)
import RegistrationSection from './landing/RegistrationSection';
function RegistrationCTA({ settings }) {
  return <RegistrationSection settings={settings} />;
}

// Footer Component
function Footer() {
  return (
    <footer className="py-12 px-6 md:px-12 lg:px-24 bg-[#292524] text-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center">
          <h3 className="font-heading text-2xl font-bold mb-4">21-Day Kalaripayattu Programme</h3>
          <p className="text-white/60 mb-8">Awaken the warrior within through the ancient martial art of Kerala</p>
          <div className="flex justify-center gap-6 text-sm text-white/60 flex-wrap">
            <a href="/kalaripayattu" className="hover:text-white transition-colors">Kalaripayattu</a>
            <a href="/terms" className="hover:text-white transition-colors">Terms & Conditions</a>
            <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="/contact" className="hover:text-white transition-colors">Contact Us</a>
          </div>
          <p className="mt-8 text-white/40 text-sm">
            © {new Date().getFullYear()} Kalaripayattu Programme. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
